import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { pushApi } from '../services/pushApi';
import { isNativeMobileApp } from './mobileApp';
import {
  isSubscriptionRenewalPushData,
  openLandlordPackageManagement,
} from './subscriptionRenewalNotification';
import { requestTenantOpenInvoice } from './tenantNotifications';

let lastRegisteredToken: string | null = null;
let initializedForUserId: string | null = null;
let listenersReady = false;

export function getLastPushToken(): string | null {
  return lastRegisteredToken;
}

const PUSH_ENABLED_KEY = 'kcn-push-enabled';
const FCM_CRASH_GUARD_KEY = 'kcn-fcm-register-guard-v2';

export function didFcmRegisterCrashLastTime(): boolean {
  try {
    return window.localStorage.getItem(FCM_CRASH_GUARD_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearFcmRegisterCrashGuard(): void {
  try {
    window.localStorage.removeItem(FCM_CRASH_GUARD_KEY);
  } catch {
    // ignore
  }
}

export function isMobilePushEnabled(userId: string): boolean {
  try {
    const value = window.localStorage.getItem(`${PUSH_ENABLED_KEY}:${userId}`);
    return value !== '0';
  } catch {
    return true;
  }
}

export function setMobilePushEnabled(userId: string, enabled: boolean): void {
  try {
    window.localStorage.setItem(`${PUSH_ENABLED_KEY}:${userId}`, enabled ? '1' : '0');
  } catch {
    // ignore
  }
}

/** Android 12 trở xuống không có quyền POST_NOTIFICATIONS — FCM dùng được luôn. */
export function isAndroidNotificationPermissionRequired(): boolean {
  const match = navigator.userAgent.match(/Android\s+(\d+)/i);
  if (!match) return Capacitor.getPlatform() === 'android';
  return Number(match[1]) >= 13;
}

/** iOS luôn hỏi quyền thông báo. Android 13+ cũng vậy. */
export function isNativeNotificationPermissionRequired(): boolean {
  if (Capacitor.getPlatform() === 'ios') return true;
  return isAndroidNotificationPermissionRequired();
}

async function waitForPushPlugin(timeoutMs = 2500): Promise<boolean> {
  if (!isNativeMobileApp()) return false;
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    if (Capacitor.isPluginAvailable('PushNotifications')) return true;
    await new Promise((resolve) => window.setTimeout(resolve, 80));
  }
  return Capacitor.isPluginAvailable('PushNotifications');
}

/** Gắn listener sớm: tap thông báo hệ thống chỉ mở app, không tự mở popup. */
export async function attachMobilePushListeners(): Promise<void> {
  try {
    if (!isNativeMobileApp() || !(await waitForPushPlugin())) return;
    await ensureListeners();
  } catch (err) {
    console.warn('[push] listeners skipped:', err);
  }
}

export async function getPushPermissionStatus(): Promise<'granted' | 'denied' | 'prompt'> {
  try {
    if (!isNativeMobileApp()) return 'prompt';
    if (!(await waitForPushPlugin())) return 'prompt';
    const perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'granted') return 'granted';
    if (perm.receive === 'denied') return 'denied';
    return 'prompt';
  } catch {
    return 'prompt';
  }
}

export async function unregisterMobilePushToken(): Promise<void> {
  if (!lastRegisteredToken) return;
  try {
    await pushApi.unregisterToken(lastRegisteredToken);
  } catch (err) {
    console.warn('[push] unregister failed:', err);
  } finally {
    lastRegisteredToken = null;
    initializedForUserId = null;
  }
}

async function ensureListeners(): Promise<void> {
  if (listenersReady) return;

  await PushNotifications.addListener('registration', async (token) => {
    const value = token.value?.trim();
    if (!value) return;
    lastRegisteredToken = value;
    try {
      await pushApi.registerToken(value, Capacitor.getPlatform());
    } catch (err) {
      console.error('[push] register token failed:', err);
    }
  });

  await PushNotifications.addListener('registrationError', (err) => {
    console.error('[push] registration error:', err);
  });

  await PushNotifications.addListener('pushNotificationReceived', () => {
    window.dispatchEvent(new CustomEvent('kcn:refresh-notifications'));
  });

  await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    window.dispatchEvent(new CustomEvent('kcn:refresh-notifications'));
    const data = (action.notification?.data || {}) as Record<string, unknown>;
    if (String(data.action || '') === 'open_invoice' || String(data.source || '') === 'tenant_rent_due') {
      requestTenantOpenInvoice();
      return;
    }
    if (isSubscriptionRenewalPushData(data)) {
      openLandlordPackageManagement();
    }
  });

  listenersReady = true;
}

/**
 * iOS / Android 13+: hỏi quyền hệ thống. FCM chưa chạy ở bước này.
 * granted = đồng ý, denied = từ chối, skipped = không hỏi được (không logout).
 */
export async function askAndroid13NotificationPermission(): Promise<'granted' | 'denied' | 'skipped'> {
  try {
    if (!isNativeMobileApp() || !isNativeNotificationPermissionRequired()) {
      return 'skipped';
    }
    if (!(await waitForPushPlugin())) {
      return 'skipped';
    }
    let receive = (await PushNotifications.checkPermissions()).receive;
    if (receive !== 'granted' && receive !== 'denied') {
      receive = (await PushNotifications.requestPermissions()).receive;
    }
    return receive === 'granted' ? 'granted' : 'denied';
  } catch (err) {
    console.warn('[push] permission prompt skipped:', err);
    return 'skipped';
  }
}

/**
 * Chỉ đăng ký FCM khi đã có quyền (iOS / Android 13+) hoặc khi Android 12- chủ động.
 * Lỗi FCM không đá session.
 */
export async function initMobilePushNotifications(
  userId: string,
  options: { requestPermission?: boolean } = {},
): Promise<boolean> {
  try {
    if (!isNativeMobileApp() || !(await waitForPushPlugin())) {
      return false;
    }
    if (initializedForUserId === userId && lastRegisteredToken) {
      return true;
    }

    const needsOsPermission = isNativeNotificationPermissionRequired();
    if (needsOsPermission) {
      let receive = (await PushNotifications.checkPermissions()).receive;
      if (receive !== 'granted' && options.requestPermission) {
        receive = (await PushNotifications.requestPermissions()).receive;
      }
      if (receive !== 'granted') {
        return false;
      }
    }

    if (didFcmRegisterCrashLastTime()) {
      console.warn('[push] skip register — lần trước FCM làm app tắt');
      return false;
    }

    initializedForUserId = userId;
    await ensureListeners();
    window.localStorage.setItem(FCM_CRASH_GUARD_KEY, '1');
    await PushNotifications.register();
    window.localStorage.removeItem(FCM_CRASH_GUARD_KEY);
    return true;
  } catch (err) {
    console.warn('[push] init skipped:', err);
    initializedForUserId = null;
    return false;
  }
}
